import type { QueryClient } from '@tanstack/react-query';

import type { ProposalsListResponse } from '../core/types';
import { proposalQueryKeys } from './keys';

/**
 * Optimistically remove a proposal from every cached pending-list query for
 * the org, decrementing `count` and the removed proposal's `filter_tag_counts`.
 *
 * Used by the action detail page after approve/dismiss so the row disappears
 * from the list immediately, before the server confirms the resolution. The
 * `results` guard skips the history views' infinite caches (InfiniteData under
 * the same lists prefix) — writing a flat `results` array into those would
 * corrupt them.
 */
export function removeProposalFromLists(
  queryClient: QueryClient,
  orgId: number,
  proposalId: number
): void {
  queryClient.setQueriesData<ProposalsListResponse>(
    { queryKey: proposalQueryKeys.lists(orgId) },
    oldData => {
      if (!oldData?.results) {
        return oldData;
      }

      const newResults = oldData.results.filter(
        action => action.id !== proposalId
      );

      const removedAction = oldData.results.find(
        action => action.id === proposalId
      );
      const newFilterTagCounts = { ...oldData.filter_tag_counts };
      if (removedAction?.filter_tags) {
        for (const tag of removedAction.filter_tags) {
          if (newFilterTagCounts[tag] !== undefined) {
            newFilterTagCounts[tag] = Math.max(
              0,
              (newFilterTagCounts[tag] ?? 0) - 1
            );
          }
        }
      }

      return {
        ...oldData,
        results: newResults,
        count: Math.max(0, (oldData.count ?? 0) - 1),
        filter_tag_counts: newFilterTagCounts,
      };
    }
  );
}
